/**
 * AssistantThread — one Ask BLIP conversation: messages, the "what I looked
 * up" steps, download buttons, and the composer. Shared by the slide-out
 * panel and the full /assistant page.
 *
 * A question runs on the server in the background; this component polls the
 * conversation every 1.5 s while a turn is running and shows the latest step.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  AssistantLink,
  AssistantMessage,
  AssistantPageContext,
  AssistantTurnStatus,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { ChatMarkdown } from './ChatMarkdown';

const POLL_MS = 1500;

interface Turn {
  turnId: string;
  question: AssistantMessage | null;
  steps: AssistantMessage[];
  answer: AssistantMessage | null;
  links: AssistantLink[];
}

function groupTurns(messages: AssistantMessage[]): Turn[] {
  const byId = new Map<string, Turn>();
  const order: Turn[] = [];
  for (const m of messages) {
    let t = byId.get(m.turnId);
    if (!t) {
      t = {
        turnId: m.turnId,
        question: null,
        steps: [],
        answer: null,
        links: [],
      };
      byId.set(m.turnId, t);
      order.push(t);
    }
    if (m.role === 'user') t.question = m;
    else if (m.kind === 'tool_result') {
      t.steps.push(m);
      for (const l of m.links)
        if (!t.links.some((x) => x.href === l.href)) t.links.push(l);
    } else if (
      m.role === 'assistant' &&
      (m.kind === 'text' || m.kind === 'error')
    )
      t.answer = m;
  }
  return order;
}

function filenameFrom(res: Response, fallback: string): string {
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return m?.[1] ?? fallback;
}

export function DownloadButton({ link }: { link: AssistantLink }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (link.kind === 'page') {
    return (
      <Link className="ab-dl ab-dl-page" to={link.href}>
        ↗ {link.label}
      </Link>
    );
  }
  const onClick = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.assistant.download(link.href);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        setErr(body?.error?.message ?? `Download failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFrom(res, `blip-download.${link.kind}`);
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setErr('Download failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="ab-dl-wrap">
      <button
        type="button"
        className="ab-dl"
        onClick={() => void onClick()}
        disabled={busy}
      >
        {busy ? 'Preparing…' : `⬇ ${link.label}`}
      </button>
      {err && <span className="ab-dl-err">{err}</span>}
    </span>
  );
}

function Steps({
  steps,
  running,
}: {
  steps: AssistantMessage[];
  running: boolean;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (steps.length === 0) return null;
  return (
    <div className="ab-steps">
      <button
        type="button"
        className="ab-steps-toggle"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? '▾' : '▸'} {running ? 'Working' : 'Looked up'}: {steps.length}{' '}
        step{steps.length === 1 ? '' : 's'}
      </button>
      {open && (
        <ol className="ab-steps-list">
          {steps.map((s) => (
            <li key={s.id} className={s.failed ? 'ab-step-bad' : ''}>
              {s.summary ?? s.text}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export interface AssistantThreadProps {
  conversationId: string | null;
  onConversation?: (id: string) => void;
  page?: AssistantPageContext;
  /** Hide the composer (admin transcript view). */
  readOnly?: boolean;
  starters?: string[];
}

export function AssistantThread({
  conversationId,
  onConversation,
  page,
  readOnly,
  starters = [],
}: AssistantThreadProps): JSX.Element {
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [status, setStatus] = useState<AssistantTurnStatus | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const lastSeq = useRef(0);
  const convRef = useRef<string | null>(conversationId);
  const endRef = useRef<HTMLDivElement>(null);

  const merge = useCallback((incoming: AssistantMessage[]) => {
    if (incoming.length === 0) return;
    lastSeq.current = Math.max(lastSeq.current, ...incoming.map((m) => m.seq));
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      return [...prev, ...incoming.filter((m) => !seen.has(m.id))];
    });
  }, []);

  // (Re)load when the conversation changes.
  useEffect(() => {
    convRef.current = conversationId;
    lastSeq.current = 0;
    setMessages([]);
    setStatus(null);
    setError(null);
    if (!conversationId) return;
    let cancelled = false;
    api.assistant
      .get(conversationId, 0)
      .then((d) => {
        if (cancelled) return;
        merge(d.messages);
        setStatus(d.turnStatus);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(
            e instanceof ApiClientError
              ? e.message
              : 'Could not load the conversation',
          );
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, merge]);

  // Poll while a turn is running.
  useEffect(() => {
    if (status !== 'running' || !convRef.current) return;
    const id = convRef.current;
    const t = setInterval(() => {
      api.assistant
        .get(id, lastSeq.current)
        .then((d) => {
          merge(d.messages);
          setStatus(d.turnStatus);
        })
        .catch(() => {
          /* transient — keep polling */
        });
    }, POLL_MS);
    return () => clearInterval(t);
  }, [status, merge]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, status]);

  const turns = useMemo(() => groupTurns(messages), [messages]);
  const running = status === 'running';

  const send = async (text: string): Promise<void> => {
    const message = text.trim();
    if (!message || running || sending) return;
    setSending(true);
    setError(null);
    try {
      const input = { message, ...(page ? { page } : {}) };
      if (convRef.current) {
        await api.assistant.send(convRef.current, input);
      } else {
        const r = await api.assistant.start(input);
        convRef.current = r.conversationId;
        onConversation?.(r.conversationId);
      }
      setDraft('');
      setStatus('running');
      // Pull the stored question immediately.
      const d = await api.assistant.get(convRef.current!, lastSeq.current);
      merge(d.messages);
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Could not send the question',
      );
    } finally {
      setSending(false);
    }
  };

  const retry = async (): Promise<void> => {
    if (!convRef.current) return;
    setError(null);
    try {
      await api.assistant.retry(convRef.current);
      setStatus('running');
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Retry failed');
    }
  };

  const latestStep = running
    ? turns[turns.length - 1]?.steps.slice(-1)[0]
    : undefined;

  return (
    <div className="ab-thread">
      <div className="ab-messages">
        {turns.length === 0 && !readOnly && (
          <div className="ab-empty">
            <p>
              Ask anything about how BLIP works or what's in it — reports,
              numbers, runs, suppliers, sources, audits, the industry dataset. I
              can hand back CSVs and short PDF briefs.
            </p>
            <p className="muted small">
              I can't change anything in BLIP. If something needs changing, I'll
              send Ramos a change request.
            </p>
            {starters.length > 0 && (
              <div className="ab-starters">
                {starters.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="ab-chip"
                    onClick={() => void send(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {turns.map((t, idx) => {
          const isLast = idx === turns.length - 1;
          return (
            <div key={t.turnId} className="ab-turn">
              {t.question && <div className="ab-q">{t.question.text}</div>}
              <Steps steps={t.steps} running={running && isLast} />
              {t.links.length > 0 && (
                <div className="ab-links">
                  {t.links.map((l) => (
                    <DownloadButton key={l.href} link={l} />
                  ))}
                </div>
              )}
              {t.answer && (
                <div
                  className={
                    t.answer.kind === 'error' ? 'ab-a ab-a-err' : 'ab-a'
                  }
                >
                  <ChatMarkdown text={t.answer.text} />
                </div>
              )}
              {isLast && running && (
                <div className="ab-working">
                  <span className="ab-dot" />{' '}
                  {latestStep ? `${latestStep.text}…` : 'Thinking…'}
                </div>
              )}
              {isLast &&
                !readOnly &&
                (status === 'failed' || status === 'interrupted') && (
                  <button
                    type="button"
                    className="btn btn-ghost ab-retry"
                    onClick={() => void retry()}
                  >
                    {status === 'interrupted' ? 'Interrupted — retry' : 'Retry'}
                  </button>
                )}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {error && <div className="form-error ab-error">{error}</div>}
      {!readOnly && (
        <form
          className="ab-composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send(draft);
          }}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(draft);
              }
            }}
            placeholder={
              running
                ? 'Ask BLIP is working…'
                : 'Ask a question…  (Enter to send, Shift+Enter for a new line)'
            }
            rows={2}
            maxLength={8000}
            disabled={running}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={running || sending || !draft.trim()}
          >
            Ask
          </button>
        </form>
      )}
    </div>
  );
}
